import {
  ClipboardDocumentCheckIcon,
  DocumentCheckIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  PencilIcon,
  CloudArrowUpIcon,
  PaperAirplaneIcon,
  XMarkIcon,
  ClockIcon,
  LinkIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/outline';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import {
  BottomNavigationBar,
  type BottomNavigationAction,
} from '../../components/BottomNavigationBar';
import { DocumentTocSidebar } from '../../components/DocumentTocSidebar';
import { MonacoMarkdownEditor } from '../../components/monaco/MonacoMarkdownEditor';
import { PageHeader } from '../../components/PageHeader';
import { StageNavigationBar } from '../../components/StageNavigationBar/StageNavigationBar';
import {
  BACK_BUTTON_LABEL,
  COMPLETE_BUTTON_LABEL,
} from '../../constants/navigation';
import { useAuth } from '../../contexts/AuthContext';
import { useUserProfile } from '../../contexts/UserProfileContext';
import { generationService } from '../../services/generationService';
import { projectService } from '../../services/projectService';
import { projectStateService } from '../../services/projectStateService';
import { reviewService } from '../../services/reviewService';
import { ArchitectureSection } from '../../types/generation';
import {
  ProjectItem,
  ProjectSection,
  ProjectStatus,
  ProjectType,
} from '../../types/project';
import { ReviewerInput, ReviewerRole, UserSearchResult } from '../../types/review';
import { buildDocumentToc } from '../../utils/documentToc';
import { buildHeadingId } from '../../utils/headingAnchors';
import {
  isCurrentUserProjectSteward,
  isProjectStewardUser,
  PROJECT_STEWARD_PARTICIPANT_MESSAGE,
} from '../../utils/reviewParticipants';
import {
  formatParticipantSummary,
  getReviewParticipantCounts,
} from '../../utils/reviewSummary';
import { getSectionInfo } from '../../utils/sectionMapping';
import { countWords } from '../../utils/wordCount';
import { PeerReviewTable } from '../ArchitectureReview/PeerReviewTable';

import {
  buildCombinedReviewMarkdown,
  parseCombinedReviewMarkdown,
} from './reviewDocumentPersistence';

const SECTION_TITLE_HEADING_REGEX = /^#\s+(.+)$/m;
// Namespaces the heading DOM ids stamped by the combined document editor so the
// table-of-contents sidebar can scroll to a section title or ADR sub-heading.
const FINAL_DOC_HEADING_PREFIX = 'final-doc';
const REVIEWER_SEARCH_MIN_LENGTH = 2;
const SELF_REVIEW_BLOCKED_MESSAGE =
  'You cannot add yourself as a participant. The document creator cannot review their own submission.';
const DUPLICATE_PARTICIPANT_MESSAGE =
  'This participant has already been added';
const SELECT_REGISTERED_USER_MESSAGE =
  'Select a registered user from the search results before adding them.';
const PARTICIPANT_ROLE_REQUIRED_MESSAGE =
  'Select whether the participant should be a reviewer or approver before adding them.';
const NO_REGISTERED_USERS_MESSAGE = 'No registered users matched this search.';
const MINIMUM_PARTICIPANTS_MESSAGE = 'Please add at least one participant';
const APPROVER_MISSING_WARNING_MESSAGE =
  'No approver yet. You can submit now and add an approver later from My Reviews.';
const SECTION_FALLBACK_PREFIX = 'Section';
const FINAL_DSI_TABLE_TITLE = 'Selected Architecture Sections';
// Shown when the user tries to enter the Archie review before every
// architecture section has been generated and selected.
const SECTION_REQUIREMENT_MESSAGE =
  'Please generate and select all architecture sections first.';
const ALL_CHANGES_SAVED_LABEL = 'All changes saved';
const UNSAVED_CHANGES_LABEL = 'Unsaved changes';
const SAVING_CHANGES_LABEL = 'Saving changes...';
const APPROVAL_STATUS_LABEL = 'Approval Status';
const NOT_SUBMITTED_STATUS = 'Not submitted for review';
const SUBMITTED_FOR_REVIEW_APPROVER_MISSING_STATUS =
  'Submitted for review, approver missing';
const SUBMITTED_FOR_APPROVAL_STATUS = 'Submitted for approval';
const APPROVED_STATUS = 'Approved';
const CHANGES_REQUESTED_STATUS = 'Changes requested';
const REVIEW_CLOSED_STATUS = 'Review closed';
const FINAL_DOCUMENT_SELECTION_UPDATE_FAILED_MESSAGE =
  'Failed to update final document sections. Please try again.';
const FINAL_DOCUMENT_SAVE_FAILED_MESSAGE =
  'Failed to save the edited document sections. Please try again.';
const PROJECT_LINE_OF_BUSINESS_LABEL = 'Project Line of Business';

type ParticipantRoleSelection = ReviewerRole | '';
type ReferenceLinkKey = 'bizzdesign_url' | 'ear_url' | 'ear_wiki_url';
const ARCHIE_CHECKLIST_TAB_ID = 'archie-checklist';
const PROJECT_TYPE_LABEL = 'Project Type';
const AUTHOR_LABEL = 'Author';
const CO_AUTHOR_LABEL = 'Co-author';
const NOT_AVAILABLE_LABEL = 'N/A';
const SECTION_SELECTION_SAVING_TOOLTIP =
  'Please wait while the final document selection is saved.';
const GREEN_FIELD_LABEL = 'Green Field';
const BROWN_FIELD_LABEL = 'Brown Field';
const DEFAULT_REVIEW_SECTION_ORDER: ArchitectureSection[] = [
  ArchitectureSection.BUSINESS_PURPOSE,
  ArchitectureSection.ADR_SUMMARY,
  ArchitectureSection.CONCEPTUAL_ARCHITECTURE,
  ArchitectureSection.INTEGRATION_ARCHITECTURE,
  ArchitectureSection.SECURITY_ARCHITECTURE,
  ArchitectureSection.TECHNOLOGY_ARCHITECTURE,
  ArchitectureSection.DATA_ARCHITECTURE,
  ArchitectureSection.THREAT_MODELING,
  ArchitectureSection.SOLUTION_SUMMARY,
];

interface ReviewSectionWordStat {
  id: string;
  title: string;
  wordCount: number | null;
  isSelected: boolean;
  isGenerated: boolean;
}

interface ProjectCollaborator {
  username: string;
  email: string;
  display_name?: string;
  line_of_business?: string | null;
  is_owner: boolean;
}

const getSearchUserLabel = (searchUser: UserSearchResult): string =>
  searchUser.email;

const formatCollaboratorName = (collaborator: ProjectCollaborator): string =>
  collaborator.display_name?.trim() || collaborator.username;

const formatCollaboratorSummary = (
  collaborator: ProjectCollaborator,
): string => {
  const collaboratorName = formatCollaboratorName(collaborator);
  const lineOfBusiness = collaborator.line_of_business?.trim();

  return lineOfBusiness
    ? `${collaboratorName} (${lineOfBusiness})`
    : collaboratorName;
};

const getSectionSummaryTitle = (
  section: ProjectSection,
  index: number,
): string => {
  if (section.title?.trim()) {
    return section.title.trim();
  }

  const titleMatch = SECTION_TITLE_HEADING_REGEX.exec(section.content ?? '');

  return titleMatch?.[1] ?? `${SECTION_FALLBACK_PREFIX} ${index + 1}`;
};

const normalizeSectionContent = (content: string): string =>
  content.replace(/\r\n?/g, '\n').trim();

const isArchitectureSection = (value: string): value is ArchitectureSection =>
  Object.values(ArchitectureSection).includes(value as ArchitectureSection);

const getApprovalStatusText = (
  reviewSummary: ProjectItem['review_summary'],
): string => {
  if (reviewSummary.status === 'not_submitted') {
    return NOT_SUBMITTED_STATUS;
  }

  if (reviewSummary.status === 'approved') {
    return APPROVED_STATUS;
  }

  if (reviewSummary.status === 'changes_requested') {
    return CHANGES_REQUESTED_STATUS;
  }

  if (reviewSummary.status === 'closed') {
    return REVIEW_CLOSED_STATUS;
  }

  if (reviewSummary.approver_missing) {
    return SUBMITTED_FOR_REVIEW_APPROVER_MISSING_STATUS;
  }

  return SUBMITTED_FOR_APPROVAL_STATUS;
};

const getProjectTypeDisplayLabel = (
  projectType: ProjectType | string,
): string => {
  if (projectType === ProjectType.BROWN_FIELD) {
    return BROWN_FIELD_LABEL;
  }

  if (projectType === ProjectType.GREEN_FIELD) {
    return GREEN_FIELD_LABEL;
  }

  return projectType;
};

const getIsProjectUnderReview = (project: ProjectItem | null): boolean =>
  project?.status === ProjectStatus.IN_REVIEW ||
  project?.review_summary.status === 'in_review';

const getCurrentReviewId = (
  activeReviewId: string | null,
  project: ProjectItem | null,
) => activeReviewId ?? project?.review_summary.review_id;

const getInitialTab = (params: URLSearchParams): string =>
  params.get('tab') || 'overview';

const OVERVIEW_SUBMIT_BUTTON_CLASS_NAME = [
  'inline-flex items-center justify-center gap-2 whitespace-nowrap',
  'px-6 py-2 rounded-lg transition-colors font-medium',
  'focus:outline-none focus:ring-2 focus:ring-offset-2',
  'bg-[#336094] text-white hover:bg-[#2a4d7a] focus:ring-[#336094]',
  'main-button disabled:bg-gray-300 disabled:text-gray-500',
  'disabled:cursor-not-allowed',
].join(' ');

// Shell-yellow "main-button" style (matches the checklist's Submit for Review
// button); same geometry as the blue overview button so they sit together.
const PEER_REVIEW_BUTTON_CLASS_NAME = [
  'inline-flex items-center justify-center gap-2 whitespace-nowrap',
  'px-6 py-2 rounded-lg transition-colors font-medium main-button',
  'disabled:opacity-50 disabled:cursor-not-allowed',
].join(' ');

const getDocumentSaveButtonClassName = (hasUnsavedChanges: boolean): string =>
  [
    'inline-flex min-w-[7.5rem] items-center justify-center gap-1.5 rounded-lg',
    'border px-2.5 py-2 text-sm font-semibold shadow-sm transition-colors',
    'focus:outline-none focus:ring-2 focus:ring-offset-2',
    hasUnsavedChanges
      ? 'border-[#008557] bg-[#008557] text-white hover:bg-[#006b45] focus:ring-[#008557]'
      : 'border-slate-200 bg-slate-50 text-slate-500',
    'disabled:cursor-default disabled:opacity-100',
    'main-button',
  ].join(' ');

const getSaveStatusMessage = (
  isSavingSelection: boolean,
  isSavingDocument: boolean,
  hasUnsavedChanges: boolean,
  lastSaved: Date,
): string => {
  if (isSavingSelection || isSavingDocument) {
    return SAVING_CHANGES_LABEL;
  }
  if (hasUnsavedChanges) {
    return UNSAVED_CHANGES_LABEL;
  }
  return `${ALL_CHANGES_SAVED_LABEL} ${lastSaved.toLocaleTimeString()}`;
};

const getSaveButtonLabel = (
  isSaving: boolean,
  hasUnsavedChanges: boolean,
): string => {
  if (isSaving) {
    return 'Saving...';
  }
  return hasUnsavedChanges ? 'Save' : 'Saved';
};

const getAuthorSummary = (
  collaborators: ProjectCollaborator[],
  project: ProjectItem,
): string => {
  const ownerCollaborator = collaborators.find(
    (collaborator) => collaborator.is_owner,
  );
  return ownerCollaborator
    ? formatCollaboratorSummary(ownerCollaborator)
    : project.owner_display_name?.trim() || project.owner;
};

const getCoAuthorSummary = (collaborators: ProjectCollaborator[]): string => {
  const coAuthors = collaborators.filter(
    (collaborator) => !collaborator.is_owner,
  );
  return coAuthors.length > 0
    ? coAuthors.map(formatCollaboratorSummary).join(', ')
    : NOT_AVAILABLE_LABEL;
};

const getProjectLineOfBusinessSummary = (project: ProjectItem): string =>
  project.projectLineOfBusiness
  ?? project.line_of_business
  ?? NOT_AVAILABLE_LABEL;

const shouldShowNoRegisteredUsersMessage = (
  reviewerEmail: string,
  selectedSearchUser: UserSearchResult | null,
  searching: boolean,
  userSearchResults: UserSearchResult[],
): boolean =>
  reviewerEmail.trim().length >= REVIEWER_SEARCH_MIN_LENGTH &&
  !selectedSearchUser &&
  !searching &&
  userSearchResults.length === 0;

const OverviewReviewAction: React.FC<{
  savingSectionSelection: boolean;
  submittingReview: boolean;
  onArchieReview: () => void;
  onPeerReview: () => void;
}> = ({
  savingSectionSelection,
  submittingReview,
  onArchieReview,
  onPeerReview,
}) => (
  <div className="flex flex-wrap items-center gap-3">
    <button
      type="button"
      onClick={onArchieReview}
      disabled={savingSectionSelection || submittingReview}
      title={
        savingSectionSelection
          ? SECTION_SELECTION_SAVING_TOOLTIP
          : 'Run Archie against the enterprise checklist before submitting '
            + 'to people'
      }
      className={OVERVIEW_SUBMIT_BUTTON_CLASS_NAME}
    >
      <ClipboardDocumentCheckIcon className="w-4 h-4" />
      {submittingReview ? 'Starting…' : 'Archie Review'}
    </button>
    <button
      type="button"
      onClick={onPeerReview}
      disabled={savingSectionSelection || submittingReview}
      title="Add reviewers and submit this architecture for peer review"
      className={PEER_REVIEW_BUTTON_CLASS_NAME}
    >
      <PaperAirplaneIcon className="w-4 h-4" />
      Peer Review
    </button>
  </div>
);

const ParticipantWarnings: React.FC<{
  participantCount: number;
  hasApprover: boolean;
}> = ({ participantCount, hasApprover }) => (
  <>
    {participantCount === 0 && (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-sm text-yellow-800 flex items-center gap-2">
          <ExclamationTriangleIcon className="w-5 h-5" />
          Please add at least one participant before submitting.
        </p>
      </div>
    )}

    {participantCount > 0 && !hasApprover && (
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800 flex items-center gap-2">
          <ExclamationTriangleIcon className="w-5 h-5" />
          {APPROVER_MISSING_WARNING_MESSAGE}
        </p>
      </div>
    )}
  </>
);

const SubmitModalFooter: React.FC<{
  participants: ReviewerInput[];
  submittingReview: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}> = ({ participants, submittingReview, onCancel, onSubmit }) => (
  <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200 bg-gray-50 flex-shrink-0">
    <div className="text-sm text-gray-600">
      {participants.length > 0 ? (
        <span className="font-medium text-green-600 flex items-center gap-2">
          <DocumentCheckIcon className="w-5 h-5" />
          {formatParticipantSummary({
            reviewerCount: participants.filter(
              (participant) => participant.role === 'reviewer',
            ).length,
            approverCount: participants.filter(
              (participant) => participant.role === 'approver',
            ).length,
          })}
        </span>
      ) : (
        <span>No participants added yet</span>
      )}
    </div>
    <div className="flex items-center gap-3">
      <button
        onClick={onCancel}
        disabled={submittingReview}
        className="px-6 py-2 border border-gray-300 rounded-lg text-gray-600 font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-white"
      >
        Cancel
      </button>
      <button
        onClick={onSubmit}
        disabled={submittingReview || participants.length === 0}
        className="px-6 py-2 bg-[#008557] text-white rounded-lg font-medium hover:bg-[#006b45] focus:outline-none focus:ring-2 focus:ring-[#008557] focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 main-button"
        title={
          participants.length === 0
            ? 'Add at least one participant to submit'
            : ''
        }
      >
        {submittingReview ? (
          <>
            <div className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>Submitting...</span>
          </>
        ) : (
          <>
            <PaperAirplaneIcon className="w-5 h-5" />
            <span>Submit for Review</span>
          </>
        )}
      </button>
    </div>
  </div>
);

/**
 * ReviewScreen - Final review and export functionality with Monaco markdown editor.
 */
export const ReviewScreen: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { profile } = useUserProfile();
  // Personas granted self-review (e.g. Admin) may add themselves as a
  // participant on their own project for internal testing.
  const canSelfReview = profile?.can_self_review ?? false;

  const tabFromUrl = getInitialTab(searchParams);
  const [activeTab, setActiveTab] = useState<string>(tabFromUrl);

  const [project, setProject] = useState<ProjectItem | null>(null);
  const [sections, setSections] = useState<ProjectSection[]>([]);
  const [selectedSectionIds, setSelectedSectionIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [markdown, setMarkdown] = useState<string>('');
  const [lastSaved, setLastSaved] = useState<Date>(new Date());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSavingDocument, setIsSavingDocument] = useState(false);
  const [documentSaveError, setDocumentSaveError] = useState<string | null>(null);

  const [showSubmitModal, setShowSubmitModal] = useState(false);
  // Popup shown when the user tries to enter the Archie review before every
  // architecture section is generated and selected.
  const [showSectionRequirementModal, setShowSectionRequirementModal] =
    useState(false);
  const [reviewerEmail, setReviewerEmail] = useState('');
  const [selectedRole, setSelectedRole] =
    useState<ParticipantRoleSelection>('');
  const [participants, setParticipants] = useState<ReviewerInput[]>([]);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [activeReviewId, setActiveReviewId] = useState<string | null>(null);
  const [collaborators, setCollaborators] = useState<ProjectCollaborator[]>([]);
  const [userSearchResults, setUserSearchResults] = useState<UserSearchResult[]>([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selectedSearchUser, setSelectedSearchUser] =
    useState<UserSearchResult | null>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const masterSectionCheckboxRef = useRef<HTMLInputElement>(null);


  const [exportingFormat, setExportingFormat] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [savingSectionSelection, setSavingSectionSelection] = useState(false);
  // Manual reference links (BizzDesign / EAR / EAR Wiki) editable in the
  // Overview tab and persisted on the project. Each shows as a clickable link
  // when set, or an "add link" affordance that expands to an input when not.
  const [linksDraft, setLinksDraft] = useState({
    bizzdesign_url: '',
    ear_url: '',
    ear_wiki_url: '',
  });
  const [editingLink, setEditingLink] = useState<ReferenceLinkKey | null>(null);
  const [savingLinks, setSavingLinks] = useState(false);
  const [linksError, setLinksError] = useState<string | null>(null);

  const showNoRegisteredUsersMessage = shouldShowNoRegisteredUsersMessage(
    reviewerEmail,
    selectedSearchUser,
    searching,
    userSearchResults,
  );

  useEffect(() => {
    const loadData = async () => {
      if (!projectId) {
        return;
      }

      try {
        setIsLoading(true);
        const [
          projectData,
          sectionsData,
          selectedSectionsData,
          collaboratorsData,
        ] = await Promise.all([
          projectService.getProject(Number.parseInt(projectId, 10)),
          projectService.getSections(Number.parseInt(projectId, 10)),
          projectService.getFinalDocumentSectionSelection(
            Number.parseInt(projectId, 10),
          ),
          projectService.getCollaborators(Number.parseInt(projectId, 10)).catch(() => []),
        ]);

        setProject(projectData);
        setSections(sectionsData);
        setSelectedSectionIds(selectedSectionsData);
        setActiveReviewId(projectData.review_summary.review_id);
        setCollaborators(collaboratorsData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [projectId]);

  useEffect(() => {
    setSearchParams({ tab: activeTab });
  }, [activeTab, setSearchParams]);

  useEffect(() => {
    if (!project) {
      return;
    }

    const selectedSectionLookup = new Set(selectedSectionIds);
    const selectedSections = sections.filter((section) =>
      selectedSectionLookup.has(section.id),
    );

    setMarkdown(buildCombinedReviewMarkdown(project, selectedSections));
  }, [project, sections, selectedSectionIds]);

  const selectedSectionLookup = new Set(selectedSectionIds);
  // Section headings that delimit the combined document, so the preview can
  // bound each "Added by Archie" green region to its own section instead of
  // bleeding into every following section.
  const selectedSectionTitles = sections
    .filter((section) => selectedSectionLookup.has(section.id))
    .map((section) => section.title);

  // Table-of-contents entries for the document editor sidebar, built from the
  // sections included in the final document. A single heading-id prefix is used
  // because the whole document renders in one combined editor.
  const documentToc = React.useMemo(
    () =>
      buildDocumentToc(
        sections.filter((section) => selectedSectionIds.includes(section.id)),
        () => FINAL_DOC_HEADING_PREFIX,
      ),
    [sections, selectedSectionIds],
  );

  // Jump the combined preview to a heading by its stamped DOM id. Section titles
  // render as `## {title}`, so a section's anchor is derived from its title.
  const scrollToFinalDocAnchor = (anchorId: string): void => {
    document
      .getElementById(anchorId)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const scrollToFinalDocSection = (sectionId: string): void => {
    const entry = documentToc.find(
      (tocEntry) => tocEntry.sectionId === sectionId,
    );
    if (entry) {
      scrollToFinalDocAnchor(
        buildHeadingId(FINAL_DOC_HEADING_PREFIX, entry.title),
      );
    }
  };

  const selectableSectionIds = sections
    .filter((section) => (section.content?.trim() ?? '').length > 0)
    .map((section) => section.id);
  const selectableSectionIdLookup = new Set(selectableSectionIds);
  const hasSelectableSections = selectableSectionIds.length > 0;
  const areAllSelectableSectionsSelected =
    hasSelectableSections &&
    selectableSectionIds.every((sectionId) => selectedSectionLookup.has(sectionId));
  const hasSomeSelectableSectionsSelected = selectableSectionIds.some(
    (sectionId) => selectedSectionLookup.has(sectionId),
  );

  useEffect(() => {
    if (!masterSectionCheckboxRef.current) {
      return;
    }

    masterSectionCheckboxRef.current.indeterminate =
      hasSomeSelectableSectionsSelected && !areAllSelectableSectionsSelected;
  }, [
    areAllSelectableSectionsSelected,
    hasSomeSelectableSectionsSelected,
  ]);

  const handleMarkdownChange = (value: string) => {
    setMarkdown(value);
    setHasUnsavedChanges(true);
    if (documentSaveError) {
      setDocumentSaveError(null);
    }
  };

  const handleSave = useCallback(async () => {
    if (!projectId || !project || isSavingDocument || !hasUnsavedChanges) {
      return;
    }

    const selectedSectionLookup = new Set(selectedSectionIds);
    const documentSections = sections.filter((section) =>
      selectedSectionLookup.has(section.id),
    );

    if (documentSections.length === 0) {
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
      setDocumentSaveError(null);
      return;
    }

    setIsSavingDocument(true);
    setDocumentSaveError(null);

    try {
      const parsedSections = parseCombinedReviewMarkdown(
        markdown,
        documentSections,
      );
      const changedSections = parsedSections.filter((parsedSection) => {
        const existingSection = documentSections.find(
          (section) => section.id === parsedSection.id,
        );

        return (
          normalizeSectionContent(existingSection?.content ?? '') !==
          normalizeSectionContent(parsedSection.content)
        );
      });

      await Promise.all(
        changedSections.map((section) => {
          if (!isArchitectureSection(section.id)) {
            throw new Error(FINAL_DOCUMENT_SAVE_FAILED_MESSAGE);
          }

          return generationService.updateSection(
            Number.parseInt(projectId, 10),
            section.id,
            section.content,
          );
        }),
      );

      const refreshedSections = await projectService.getSections(
        Number.parseInt(projectId, 10),
      );

      setSections(refreshedSections);
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
    } catch (err) {
      setDocumentSaveError(
        err instanceof Error
          ? err.message
          : FINAL_DOCUMENT_SAVE_FAILED_MESSAGE,
      );
    } finally {
      setIsSavingDocument(false);
    }
  }, [
    hasUnsavedChanges,
    isSavingDocument,
    markdown,
    project,
    projectId,
    sections,
    selectedSectionIds,
  ]);

  useEffect(() => {
    const AUTO_SAVE_INTERVAL = 30000;
    const autoSaveInterval = setInterval(() => {
      if (hasUnsavedChanges && !isSavingDocument) {
        void handleSave();
      }
    }, AUTO_SAVE_INTERVAL);

    return () => clearInterval(autoSaveInterval);
  }, [handleSave, hasUnsavedChanges, isSavingDocument]);

  const updateSelectedSections = async (
    nextSelectedSectionIds: string[],
    previousSelectedSectionIds: string[],
  ) => {
    if (!projectId) {
      return;
    }

    setSelectedSectionIds(nextSelectedSectionIds);
    setSavingSectionSelection(true);

    try {
      const savedSectionIds =
        await projectService.updateFinalDocumentSectionSelection(
          Number.parseInt(projectId, 10),
          nextSelectedSectionIds,
        );

      setSelectedSectionIds(savedSectionIds);
      setLastSaved(new Date());
      setHasUnsavedChanges(false);
    } catch {
      setSelectedSectionIds(previousSelectedSectionIds);
      alert(FINAL_DOCUMENT_SELECTION_UPDATE_FAILED_MESSAGE);
    } finally {
      setSavingSectionSelection(false);
    }
  };

  const handleSectionSelectionToggle = async (sectionId: string) => {
    const nextSelectedSectionIds = selectedSectionIds.includes(sectionId)
      ? selectedSectionIds.filter((selectedId) => selectedId !== sectionId)
      : [...selectedSectionIds, sectionId];

    await updateSelectedSections(nextSelectedSectionIds, selectedSectionIds);
  };

  const handleExport = async (format: string) => {
    if (!projectId || savingSectionSelection) {
      return;
    }

    setExportingFormat(format);
    setExportError(null);

    try {
      const blob = await generationService.exportDocument(
        Number.parseInt(projectId, 10),
        format,
      );
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      const safeTitle = project?.title
        .replace(/[^a-z0-9]/gi, '_')
        .toLowerCase() ?? 'document';
      const extMap: Record<string, string> = {
        markdown: '.md',
        pdf: '.pdf',
        docx: '.docx',
      };
      link.download = `${safeTitle}${extMap[format] ?? '.md'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Export failed';
      setExportError(message);
    } finally {
      setExportingFormat(null);
    }
  };

  // Keep the editable link inputs in sync with the loaded/saved project.
  useEffect(() => {
    setLinksDraft({
      bizzdesign_url: project?.bizzdesign_url ?? '',
      ear_url: project?.ear_url ?? '',
      ear_wiki_url: project?.ear_wiki_url ?? '',
    });
  }, [project?.bizzdesign_url, project?.ear_url, project?.ear_wiki_url]);

  const startEditLink = (key: ReferenceLinkKey) => {
    setLinksError(null);
    setLinksDraft((draft) => ({ ...draft, [key]: project?.[key] ?? '' }));
    setEditingLink(key);
  };

  const cancelEditLink = () => {
    setLinksError(null);
    setEditingLink(null);
    setLinksDraft({
      bizzdesign_url: project?.bizzdesign_url ?? '',
      ear_url: project?.ear_url ?? '',
      ear_wiki_url: project?.ear_wiki_url ?? '',
    });
  };

  const handleSaveLinks = async () => {
    if (!projectId) {
      return;
    }
    setSavingLinks(true);
    setLinksError(null);
    try {
      const updated = await projectService.updateProject(
        Number.parseInt(projectId, 10),
        {
          bizzdesign_url: linksDraft.bizzdesign_url.trim() || null,
          ear_url: linksDraft.ear_url.trim() || null,
          ear_wiki_url: linksDraft.ear_wiki_url.trim() || null,
        },
      );
      setProject((previous) =>
        previous
          ? {
            ...previous,
            bizzdesign_url: updated.bizzdesign_url ?? null,
            ear_url: updated.ear_url ?? null,
            ear_wiki_url: updated.ear_wiki_url ?? null,
          }
          : previous,
      );
      setEditingLink(null);
    } catch (linkSaveError) {
      setLinksError(
        linkSaveError instanceof Error
          ? linkSaveError.message
          : 'Failed to save link.',
      );
    } finally {
      setSavingLinks(false);
    }
  };

  const handleStageClick = (_stageId: number, route: string) => {
    navigate(route);
  };

  const handleAddReviewer = () => {
    if (!selectedSearchUser) {
      if (reviewerEmail.trim()) {
        alert(SELECT_REGISTERED_USER_MESSAGE);
      }
      return;
    }

    if (!selectedRole) {
      alert(PARTICIPANT_ROLE_REQUIRED_MESSAGE);
      return;
    }

    if (project && isProjectStewardUser(selectedSearchUser, project)) {
      if (!canSelfReview) {
        alert(PROJECT_STEWARD_PARTICIPANT_MESSAGE);
        return;
      }
    }

    const email = selectedSearchUser.email;
    if (
      !canSelfReview &&
      email.toLowerCase() === user?.email?.toLowerCase()
    ) {
      alert(SELF_REVIEW_BLOCKED_MESSAGE);
      return;
    }
    if (participants.some((participant) => participant.email === email)) {
      alert(DUPLICATE_PARTICIPANT_MESSAGE);
      return;
    }

    setParticipants([...participants, { email, role: selectedRole }]);
    setReviewerEmail('');
    setSelectedRole('');
    setSelectedSearchUser(null);
    setUserSearchResults([]);
    setSearchOpen(false);
  };

  const handleSelectUser = (selectedUser: UserSearchResult) => {
    if (
      project &&
      !canSelfReview &&
      isProjectStewardUser(selectedUser, project)
    ) {
      alert(PROJECT_STEWARD_PARTICIPANT_MESSAGE);
      setReviewerEmail('');
      setSelectedSearchUser(null);
      setUserSearchResults([]);
      setSearchOpen(false);
      return;
    }

    if (
      !canSelfReview &&
      selectedUser.email.toLowerCase() === user?.email?.toLowerCase()
    ) {
      alert(SELF_REVIEW_BLOCKED_MESSAGE);
      setReviewerEmail('');
      setSelectedSearchUser(null);
      setUserSearchResults([]);
      setSearchOpen(false);
      return;
    }

    if (participants.some((participant) => participant.email === selectedUser.email)) {
      alert(DUPLICATE_PARTICIPANT_MESSAGE);
      setReviewerEmail('');
      setSelectedSearchUser(null);
      setUserSearchResults([]);
      setSearchOpen(false);
      return;
    }

    setSelectedSearchUser(selectedUser);
    setReviewerEmail(getSearchUserLabel(selectedUser));
    setUserSearchResults([]);
    setSearchOpen(false);
  };

  useEffect(() => {
    const trimmed = reviewerEmail.trim();
    if (
      selectedSearchUser &&
      trimmed === getSearchUserLabel(selectedSearchUser)
    ) {
      setUserSearchResults([]);
      setSearchOpen(false);
      return;
    }

    if (trimmed.length < REVIEWER_SEARCH_MIN_LENGTH) {
      setUserSearchResults([]);
      setSearchOpen(false);
      return;
    }

    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const results = await reviewService.searchUsers(trimmed);
        setUserSearchResults(results);
        setSearchOpen(results.length > 0);
      } catch {
        setUserSearchResults([]);
        setSearchOpen(false);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [project, reviewerEmail, selectedSearchUser]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setSearchOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleRemoveReviewer = (email: string) => {
    setParticipants(participants.filter((participant) => participant.email !== email));
  };

  const handleToggleRole = (email: string) => {
    setParticipants(
      participants.map((participant) => {
        if (participant.email !== email) {
          return participant;
        }

        const nextRole =
          participant.role === 'reviewer' ? 'approver' : 'reviewer';

        return {
          ...participant,
          role: nextRole,
        };
      }),
    );
  };

  const handleSubmitForReview = async () => {
    if (!projectId || !project || participants.length === 0) {
      alert(MINIMUM_PARTICIPANTS_MESSAGE);
      return;
    }

    try {
      setSubmittingReview(true);

      const createdReview = await reviewService.createReview({
        project_id: Number.parseInt(projectId, 10),
        reviewers: participants,
      });
      const participantCounts = getReviewParticipantCounts(createdReview.reviewers);

      await projectStateService.updateProjectStatus(
        Number.parseInt(projectId, 10),
        ProjectStatus.IN_REVIEW,
      );
      setProject({
        ...project,
        status: ProjectStatus.IN_REVIEW,
        review_summary: {
          review_id: createdReview.id,
          status: 'in_review',
          reviewer_count: participantCounts.reviewerCount,
          approver_count: participantCounts.approverCount,
          approver_missing: participantCounts.approverCount === 0,
          approver_names: [],
          approved_at: null,
          updated_at: createdReview.updated_at,
        },
      });
      setActiveReviewId(createdReview.id);

      setShowSubmitModal(false);
      setParticipants([]);
      alert('Architecture submitted for review successfully!');
      navigate('/?tab=reviews');
    } catch {
      alert('Failed to submit for review. Please try again.');
    } finally {
      setSubmittingReview(false);
    }
  };

  // Reload the project sections after Archie applies a fix so the combined
  // document (rebuilt by the markdown effect) reflects the change.
  const reloadSections = useCallback(async () => {
    if (!projectId) {
      return;
    }
    const refreshedSections = await projectService.getSections(
      Number.parseInt(projectId, 10),
    );
    setSections(refreshedSections);
  }, [projectId]);

  // "Let Archie Review": create the review with no human reviewers yet (the
  // Archie-only phase) and open the Archie Review Checklist tab in place.
  // Reviewers are added later via "Submit for Review".
  const handleLetArchieReview = async () => {
    if (!projectId || !project) {
      return;
    }
    try {
      setSubmittingReview(true);
      // create_review is idempotent: it returns the project's existing active
      // review if one already exists, so this no longer fails on a second run.
      const createdReview = await reviewService.createReview({
        project_id: Number.parseInt(projectId, 10),
        reviewers: [],
      });
      setActiveReviewId(createdReview.id);
      // Marking the project In Review is best-effort - the checklist can open
      // even if the status sync fails, since the review already exists.
      try {
        await projectStateService.updateProjectStatus(
          Number.parseInt(projectId, 10),
          ProjectStatus.IN_REVIEW,
        );
      } catch {
        /* non-fatal: the review was created; status sync can catch up later */
      }
      setActiveTab(ARCHIE_CHECKLIST_TAB_ID);
    } catch (reviewError) {
      alert(
        reviewError instanceof Error
          ? `Failed to start the Archie review: ${reviewError.message}`
          : 'Failed to start the Archie review. Please try again.',
      );
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleComplete = () => {
    navigate('/');
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Error Loading Data
          </h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => globalThis.location.reload()}
            className="btn btn-primary main-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const currentReviewId = getCurrentReviewId(activeReviewId, project);
  // Once the architecture has been submitted for peer review WITH human
  // participants, the Peer Review buttons jump straight to the review page
  // (where the selected reviewers/approvers are listed and editable) instead of
  // re-opening an empty submit modal. An Archie-only review (no participants
  // yet) still opens the modal so participants can be added.
  const peerReviewParticipantCount =
    (project?.review_summary?.reviewer_count ?? 0) +
    (project?.review_summary?.approver_count ?? 0);
  const hasSubmittedPeerReview =
    Boolean(project?.review_summary?.review_id) &&
    peerReviewParticipantCount > 0;
  const handlePeerReviewClick = () => {
    // ReviewDetailPage reads projectId from a query param, so it must be
    // included or the page loads blank.
    if (hasSubmittedPeerReview && currentReviewId && projectId) {
      navigate(`/reviews/${currentReviewId}?projectId=${projectId}`);
      return;
    }
    setShowSubmitModal(true);
  };
  // Owner/co-owner: the only role that runs Archie and applies fixes here.
  const isProjectSteward = isCurrentUserProjectSteward(user, project);
  const isProjectUnderReview = getIsProjectUnderReview(project);

  if (isLoading || !project) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg text-primary"></div>
          <p className="mt-4 text-gray-600">Loading project data...</p>
        </div>
      </div>
    );
  }

  // The final review is part of the project-creation flow: only the project
  // owner or a co-owner may access it (they run Archie and submit for review).
  if (!isProjectSteward) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md text-center">
          <ExclamationTriangleIcon className="mx-auto mb-4 h-12 w-12 text-amber-500" />
          <h2 className="mb-2 text-xl font-semibold text-gray-900">
            Restricted to the project owner
          </h2>
          <p className="mb-4 text-gray-600">
            Only the project owner or a co-owner can access the final review and
            submit this architecture for peer review.
          </p>
          <button
            onClick={() => navigate('/')}
            className="btn btn-primary main-button"
          >
            Back to projects
          </button>
        </div>
      </div>
    );
  }

  const approvalStatusText = getApprovalStatusText(project.review_summary);
  const generatedSectionsById = new Map(
    sections.map((section) => [section.id, section] as const),
  );
  const defaultReviewSectionIds = new Set<string>(DEFAULT_REVIEW_SECTION_ORDER);
  const sectionWordStats: ReviewSectionWordStat[] = [
    ...DEFAULT_REVIEW_SECTION_ORDER.map((sectionId, index) => {
      const section = generatedSectionsById.get(sectionId);
      const trimmedContent = section?.content?.trim() ?? '';
      const isGenerated = trimmedContent.length > 0;

      return {
        id: sectionId,
        title: section
          ? getSectionSummaryTitle(section, index)
          : getSectionInfo(sectionId).title,
        wordCount: isGenerated ? countWords(trimmedContent) : null,
        isSelected: isGenerated && selectedSectionLookup.has(sectionId),
        isGenerated,
      };
    }),
    ...sections
      .filter((section) => !defaultReviewSectionIds.has(section.id))
      .map((section, index) => {
        const trimmedContent = section.content?.trim() ?? '';
        const isGenerated = trimmedContent.length > 0;

        return {
          id: section.id,
          title: getSectionSummaryTitle(
            section,
            DEFAULT_REVIEW_SECTION_ORDER.length + index,
          ),
          wordCount: isGenerated ? countWords(trimmedContent) : null,
          isSelected: isGenerated && selectedSectionLookup.has(section.id),
          isGenerated,
        };
      }),
  ];
  const selectedSectionWordCount = sectionWordStats.reduce(
    (totalWordCount, sectionStat) => {
      if (!sectionStat.isSelected || sectionStat.wordCount === null) {
        return totalWordCount;
      }

      return totalWordCount + sectionStat.wordCount;
    },
    0,
  );
  // The Archie review can only be entered once every architecture section has
  // been generated in the previous step AND selected in the "Selected
  // Architecture Sections" list. Otherwise the review would run against an
  // incomplete document.
  const hasReviewSections = sectionWordStats.length > 0;
  const areAllSectionsGenerated =
    hasReviewSections && sectionWordStats.every((stat) => stat.isGenerated);
  const areAllSectionsSelected =
    hasReviewSections && sectionWordStats.every((stat) => stat.isSelected);
  const canEnterArchieReview =
    areAllSectionsGenerated && areAllSectionsSelected;
  const saveStatusMessage = getSaveStatusMessage(
    savingSectionSelection,
    isSavingDocument,
    hasUnsavedChanges,
    lastSaved,
  );
  const totalWordCountMessage =
    `Word Count: ${selectedSectionWordCount.toLocaleString()}`;
  const hasApprover = participants.some(
    (participant) => participant.role === 'approver',
  );
  const authorSummary = getAuthorSummary(collaborators, project);
  const coAuthorSummary = getCoAuthorSummary(collaborators);
  const projectLineOfBusinessSummary = getProjectLineOfBusinessSummary(project);
  const completePrimaryAction: BottomNavigationAction = {
    label: COMPLETE_BUTTON_LABEL,
    onClick: handleComplete,
    variant: 'primary' as const,
    icon: <DocumentCheckIcon className="w-5 h-5" />,
  };
  const documentSaveButtonClassName =
    getDocumentSaveButtonClassName(hasUnsavedChanges);
  const bottomPrimaryActions: BottomNavigationAction[] = [completePrimaryAction];

  const handleToggleAllSectionSelections = async () => {
    if (!hasSelectableSections) {
      return;
    }

    const nonSelectableSelectedSectionIds = selectedSectionIds.filter(
      (sectionId) => !selectableSectionIdLookup.has(sectionId),
    );
    const nextSelectedSectionIds = areAllSelectableSectionsSelected
      ? nonSelectableSelectedSectionIds
      : [...nonSelectableSelectedSectionIds, ...selectableSectionIds];

    await updateSelectedSections(nextSelectedSectionIds, selectedSectionIds);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <StageNavigationBar
        currentStage={7}
        projectId={Number.parseInt(projectId!, 10)}
        isBrownfield={project?.projectType === ProjectType.BROWN_FIELD}
        projectStatus={(() => {
          const statusMap: Record<string, ProjectStatus> = {
            context_upload: ProjectStatus.CONTEXT_UPLOAD,
            context_review: ProjectStatus.CONTEXT_REVIEW,
            adr: ProjectStatus.ADR,
            generation: ProjectStatus.GENERATION,
            bizzdesign_integration: ProjectStatus.BIZZDESIGN_INTEGRATION,
            in_review: ProjectStatus.IN_REVIEW,
            completed: ProjectStatus.COMPLETED,
            published: ProjectStatus.PUBLISHED,
          };

          return statusMap[project.status] ?? ProjectStatus.CONTEXT_UPLOAD;
        })()}
        onStageClick={handleStageClick}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="mb-3 [&>div]:mb-0">
          <PageHeader
            title="Final Review & Export"
            subtitle={`Complete document review and export preparation for ${project.title}`}
            icon={DocumentCheckIcon}
            iconBgColor="#008557"
          >
            {isProjectUnderReview && (
              <div className="mt-2 flex items-center">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 border border-blue-200 shadow-sm animate-pulse-slow">
                  <ClockIcon className="w-4 h-4" />
                  Status: In Review
                </span>
                <span className="ml-3 text-sm text-gray-500">
                  This project is currently under architecture review.
                </span>
              </div>
            )}
          </PageHeader>
        </div>

        <div className="border-b border-gray-200 mb-6">
          <nav className="flex space-x-8" aria-label="Tabs">
            {[
              { id: 'overview', name: 'Overview', icon: EyeIcon },
              {
                id: ARCHIE_CHECKLIST_TAB_ID,
                name: 'Archie Review Checklist',
                icon: ClipboardDocumentCheckIcon,
              },
              { id: 'document', name: 'Document Editor', icon: PencilIcon },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? 'border-[#008557] text-[#008557]'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="mb-6 flex flex-col gap-6 lg:flex-row lg:items-stretch lg:justify-between">
                <div className="min-w-0 lg:flex-1">
                  <h3 className="text-2xl font-bold text-gray-900">
                    {project.title}
                  </h3>
                  <div className="mt-3 space-y-1 text-sm text-gray-600">
                    <p>
                      <strong>{PROJECT_TYPE_LABEL}:</strong>{' '}
                      {getProjectTypeDisplayLabel(project.project_type)}
                    </p>
                    <p>
                      <strong>{PROJECT_LINE_OF_BUSINESS_LABEL}:</strong>{' '}
                      {projectLineOfBusinessSummary}
                    </p>
                    <p>
                      <strong>{AUTHOR_LABEL}:</strong>{' '}
                      {authorSummary}
                    </p>
                    <p>
                      <strong>{CO_AUTHOR_LABEL}:</strong>{' '}
                      {coAuthorSummary}
                    </p>
                    <p>
                      <strong>{APPROVAL_STATUS_LABEL}:</strong>{' '}
                      {approvalStatusText}
                    </p>
                  </div>
                  <div className="mt-3">
                    <OverviewReviewAction
                      savingSectionSelection={savingSectionSelection}
                      submittingReview={submittingReview}
                      onArchieReview={() => {
                        if (!canEnterArchieReview) {
                          setShowSectionRequirementModal(true);
                          return;
                        }
                        if (currentReviewId) {
                          setActiveTab(ARCHIE_CHECKLIST_TAB_ID);
                        } else {
                          void handleLetArchieReview();
                        }
                      }}
                      onPeerReview={handlePeerReviewClick}
                    />
                  </div>
                </div>

                {/* Reference links fill the right side of the box; entered
                    manually by the owner. Export buttons pin to the bottom so
                    they line up with the review buttons on the left. */}
                <div className="flex w-full flex-col lg:max-w-sm lg:flex-shrink-0">
                  <h4 className="mb-3 font-medium text-gray-900">
                    Reference Links
                  </h4>
                  <div className="space-y-2">
                    {([
                      ['bizzdesign_url', 'Link to BizzDesign'],
                      ['ear_url', 'Link to EAR'],
                      ['ear_wiki_url', 'Link to EAR Wiki'],
                    ] as const).map(([key, label]) => {
                      const url = (project?.[key] ?? '').trim();
                      if (editingLink === key) {
                        return (
                          <div key={key} className="flex flex-col gap-1">
                            <span className="text-sm font-medium text-gray-700">
                              {label}
                            </span>
                            <div className="flex items-center gap-2">
                              <input
                                type="url"
                                autoFocus
                                aria-label={label}
                                value={linksDraft[key]}
                                onChange={(event) => {
                                  const { value } = event.target;
                                  setLinksDraft((draft) => ({
                                    ...draft,
                                    [key]: value,
                                  }));
                                }}
                                placeholder="https://…"
                                className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 shadow-sm transition-colors focus:border-[#336094] focus:outline-none focus:ring-1 focus:ring-[#336094]"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  void handleSaveLinks();
                                }}
                                disabled={savingLinks}
                                className="main-button inline-flex items-center rounded-lg px-3 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                {savingLinks ? '…' : 'Save'}
                              </button>
                              <button
                                type="button"
                                onClick={cancelEditLink}
                                aria-label="Cancel"
                                className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                              >
                                <XMarkIcon className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        );
                      }
                      if (url) {
                        return (
                          <div key={key} className="flex items-center gap-1">
                            <a
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1.5 text-sm font-medium text-[#336094] hover:underline"
                            >
                              <LinkIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                              {label}
                            </a>
                            <button
                              type="button"
                              onClick={() => startEditLink(key)}
                              aria-label={`Edit ${label}`}
                              className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                            >
                              <PencilIcon className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        );
                      }
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => startEditLink(key)}
                          className="flex w-fit items-center gap-1.5 text-sm text-gray-500 transition-colors hover:text-gray-700"
                        >
                          <PlusCircleIcon className="h-4 w-4 flex-shrink-0 text-gray-400" />
                          {label}
                        </button>
                      );
                    })}
                  </div>
                  {linksError && (
                    <p className="mt-2 text-xs text-red-600">{linksError}</p>
                  )}
                  {/* Export buttons pinned to the bottom, left-aligned with the
                      links so they line up with the review buttons. */}
                  <div className="mt-auto flex flex-col items-start gap-1 pt-6">
                    <div className="flex flex-wrap gap-2">
                      {[
                        { format: 'markdown', label: 'Markdown' },
                        { format: 'pdf', label: 'PDF' },
                        { format: 'docx', label: 'Word' },
                      ].map(({ format, label }) => {
                        const isExporting = exportingFormat === format;
                        const isDisabled =
                          exportingFormat !== null || savingSectionSelection;
                        return (
                          <button
                            key={format}
                            type="button"
                            onClick={() => {
                              void handleExport(format);
                            }}
                            disabled={isDisabled}
                            title={`Download the final document as ${label}`}
                            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-[#336094] hover:text-[#336094] disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {isExporting ? (
                              <span className="loading loading-spinner loading-xs" />
                            ) : (
                              <DocumentArrowDownIcon className="h-4 w-4" />
                            )}
                            {isExporting ? `Exporting ${label}…` : label}
                          </button>
                        );
                      })}
                    </div>
                    {exportError && (
                      <span className="text-xs text-red-600">{exportError}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-6 border-t border-gray-200 pt-6">
                <div className="w-full overflow-hidden rounded-lg border border-gray-200">
                  <div className="border-b border-gray-200 bg-gray-50 px-4 py-3 flex items-start justify-between gap-4">
                    <div className="flex items-center">
                      <div className="w-12 flex-shrink-0">
                        <input
                          ref={masterSectionCheckboxRef}
                          type="checkbox"
                          checked={areAllSelectableSectionsSelected}
                          onChange={() => {
                            void handleToggleAllSectionSelections();
                          }}
                          disabled={savingSectionSelection || !hasSelectableSections}
                          aria-label="Select or deselect all generated sections"
                          className="checkbox checkbox-sm rounded border-gray-300 text-[#008557] [--chkbg:#008557] [--chkfg:white] disabled:border-gray-200 disabled:bg-gray-100"
                        />
                      </div>
                      <h4 className="font-medium text-gray-900">
                        {FINAL_DSI_TABLE_TITLE}
                      </h4>
                    </div>
                    <div className="flex flex-col items-end text-right">
                      <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">
                        {totalWordCountMessage}
                      </span>
                      <span className="text-xs text-gray-500 whitespace-nowrap">
                        {saveStatusMessage}
                      </span>
                    </div>
                  </div>
                  <table className="min-w-full text-sm">
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {sectionWordStats.map((sectionStat) => (
                        <tr
                          key={sectionStat.id}
                          className={sectionStat.isGenerated ? 'bg-white' : 'bg-gray-50'}
                        >
                          <td className="px-4 py-3 w-12 align-middle">
                            <input
                              type="checkbox"
                              checked={sectionStat.isSelected}
                              onChange={() => {
                                void handleSectionSelectionToggle(sectionStat.id);
                              }}
                              disabled={savingSectionSelection || !sectionStat.isGenerated}
                              aria-label={`Include ${sectionStat.title} in the final document`}
                              className="checkbox checkbox-sm rounded border-gray-300 text-[#008557] [--chkbg:#008557] [--chkfg:white] disabled:border-gray-200 disabled:bg-gray-100"
                            />
                          </td>
                          <td
                            className={`px-4 py-3 font-medium ${
                              sectionStat.isGenerated ? 'text-gray-700' : 'text-gray-400'
                            }`}
                          >
                            {sectionStat.title}
                          </td>
                          <td
                            className={`px-4 py-3 text-right whitespace-nowrap ${
                              sectionStat.isGenerated ? 'text-gray-500' : 'text-gray-400'
                            }`}
                          >
                            {sectionStat.wordCount === null
                              ? ''
                              : `${sectionStat.wordCount.toLocaleString()} words`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === ARCHIE_CHECKLIST_TAB_ID &&
          (currentReviewId ? (
            <PeerReviewTable
              reviewId={currentReviewId}
              projectId={projectId ? Number.parseInt(projectId, 10) : 0}
              canApprove={isProjectSteward}
              canUseChat={isProjectSteward}
              canEditArchitectReview={isProjectSteward}
              canFix={isProjectSteward}
              showPeerReviewColumn={false}
              onFixCompleted={reloadSections}
              onOpenFixSection={() => setActiveTab('document')}
              showSubmitForReview={isProjectSteward}
              onSubmitForReview={handlePeerReviewClick}
            />
          ) : (
            <div className="rounded-lg border border-gray-200 bg-white p-10 text-center shadow-sm">
              <ClipboardDocumentCheckIcon className="mx-auto h-10 w-10 text-[#008557]" />
              <h3 className="mt-4 text-lg font-semibold text-gray-900">
                Let Archie review this architecture
              </h3>
              <p className="mx-auto mt-1 max-w-md text-sm text-gray-600">
                Archie checks the document against the enterprise checklist and
                can apply grounded fixes before you submit it for peer review.
              </p>
              <div className="mt-5 flex justify-center">
                <button
                  type="button"
                  onClick={handleLetArchieReview}
                  disabled={submittingReview || savingSectionSelection}
                  className={OVERVIEW_SUBMIT_BUTTON_CLASS_NAME}
                >
                  <PaperAirplaneIcon className="h-4 w-4" />
                  {submittingReview ? 'Starting…' : 'Let Archie Review'}
                </button>
              </div>
            </div>
          ))}

        {activeTab === 'document' && (
          <div className="flex flex-col gap-6 lg:flex-row lg:items-start">
            {/* Table-of-contents sidebar: jump to any section or ADR. */}
            <DocumentTocSidebar
              toc={documentToc}
              onSelectSection={scrollToFinalDocSection}
              onSelectHeading={(_sectionId, headingId) =>
                scrollToFinalDocAnchor(headingId)
              }
            />

            <div className="min-w-0 flex-1 space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex flex-col items-start gap-1.5">
                  <button
                    onClick={() => {
                      void handleSave();
                    }}
                    disabled={!hasUnsavedChanges || isSavingDocument}
                    className={documentSaveButtonClassName}
                  >
                    <CloudArrowUpIcon className="w-4 h-4" />
                    {getSaveButtonLabel(isSavingDocument, hasUnsavedChanges)}
                  </button>
                  <span className="pl-1 text-xs text-gray-500">
                    Last saved: {lastSaved.toLocaleTimeString()}
                  </span>
                  {documentSaveError && (
                    <span className="pl-1 text-xs text-red-600">
                      {documentSaveError}
                    </span>
                  )}
                </div>

                {/* Download buttons, taken over from the Overview tab. */}
                <div className="flex flex-col items-end gap-1">
                  <div className="flex flex-wrap gap-2">
                    {[
                      { format: 'markdown', label: 'Markdown' },
                      { format: 'pdf', label: 'PDF' },
                      { format: 'docx', label: 'Word' },
                    ].map(({ format, label }) => {
                      const isExporting = exportingFormat === format;
                      const isDisabled =
                        exportingFormat !== null || savingSectionSelection;
                      return (
                        <button
                          key={format}
                          type="button"
                          onClick={() => {
                            void handleExport(format);
                          }}
                          disabled={isDisabled}
                          title={`Download the final document as ${label}`}
                          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:border-[#336094] hover:text-[#336094] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isExporting ? (
                            <span className="loading loading-spinner loading-xs" />
                          ) : (
                            <DocumentArrowDownIcon className="h-4 w-4" />
                          )}
                          {isExporting ? `Exporting ${label}…` : label}
                        </button>
                      );
                    })}
                  </div>
                  {exportError && (
                    <span className="text-xs text-red-600">{exportError}</span>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <MonacoMarkdownEditor
                  value={markdown}
                  onChange={handleMarkdownChange}
                  height="800px"
                  theme="light"
                  placeholder="Start writing your architecture documentation..."
                  iconOnlyButtons={true}
                  defaultViewMode="preview"
                  sectionBoundaryTitles={selectedSectionTitles}
                  headingIdPrefix={FINAL_DOC_HEADING_PREFIX}
                />
              </div>
            </div>
          </div>
        )}

        <BottomNavigationBar
          backAction={{
            label: BACK_BUTTON_LABEL,
            onClick: () => navigate(`/project/${projectId}/generation`),
          }}
          primaryActions={bottomPrimaryActions}
        />
      </div>

      {showSectionRequirementModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowSectionRequirementModal(false)}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                setShowSectionRequirementModal(false);
              }
            }}
          />

          <div className="relative z-10 mx-4 w-full max-w-md overflow-hidden rounded-lg bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-900">
                Complete your sections first
              </h2>
              <button
                type="button"
                onClick={() => setShowSectionRequirementModal(false)}
                aria-label="Close"
                className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6">
              <div className="flex items-start gap-3">
                <ExclamationTriangleIcon className="mt-0.5 h-6 w-6 flex-shrink-0 text-amber-500" />
                <p className="text-sm text-gray-700">
                  {SECTION_REQUIREMENT_MESSAGE}
                </p>
              </div>
            </div>

            <div className="flex justify-end border-t border-gray-200 p-4">
              <button
                type="button"
                onClick={() => setShowSectionRequirementModal(false)}
                className="main-button rounded-md px-4 py-2 text-sm font-medium"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {showSubmitModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowSubmitModal(false)}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                setShowSubmitModal(false);
              }
            }}
          />

          <div className="relative z-10 w-full max-w-2xl mx-4 bg-white rounded-lg shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0 bg-white">
              <h2 className="text-xl font-semibold text-gray-900">Submit for Review</h2>
              <button
                onClick={() => setShowSubmitModal(false)}
                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 space-y-6 overflow-y-auto">
              <p className="text-gray-600">
                Add participants who will evaluate this architecture document.
                <strong> Reviewers</strong> can leave comments.
                <strong> Approvers</strong> can leave comments and approve.
              </p>

              <div ref={searchRef}>
                <label
                  htmlFor="reviewer-search"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Add Participant
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      id="reviewer-search"
                      type="text"
                      value={reviewerEmail}
                      onChange={(event) => {
                        setReviewerEmail(event.target.value);
                        setSelectedSearchUser(null);
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          handleAddReviewer();
                        }
                      }}
                      onFocus={() => userSearchResults.length > 0 && setSearchOpen(true)}
                      placeholder="Search registered users by name or email…"
                      autoComplete="off"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0078d4] focus:border-[#0078d4]"
                    />
                    {searching && (
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                        Searching…
                      </span>
                    )}
                    {searchOpen && userSearchResults.length > 0 && (
                      <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                        {userSearchResults.map((searchUser) => (
                          <li
                            key={searchUser.email}
                            onMouseDown={() => handleSelectUser(searchUser)}
                            className="cursor-pointer px-4 py-2 hover:bg-gray-50"
                          >
                            <div className="flex flex-col min-w-0">
                              <span className="text-sm font-medium text-gray-900 truncate">
                                {searchUser.display_name ?? searchUser.username}
                              </span>
                              <span className="text-xs text-gray-500 truncate">
                                {searchUser.email}
                              </span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <select
                    id="reviewer-role"
                    value={selectedRole}
                    onChange={(event) =>
                      setSelectedRole(
                        event.target.value as ParticipantRoleSelection,
                      )
                    }
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0078d4] text-sm"
                  >
                    <option value="" disabled>
                      Select role
                    </option>
                    <option value="reviewer">Reviewer</option>
                    <option value="approver">Approver</option>
                  </select>
                  <button
                    onClick={handleAddReviewer}
                    disabled={!selectedSearchUser || !selectedRole}
                    className="px-4 py-2 bg-[#0078d4] text-white rounded-lg hover:bg-[#0060aa] transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    Add
                  </button>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Search registered users and select one result before clicking Add.
                </p>
                {showNoRegisteredUsersMessage && (
                  <p className="text-sm text-amber-700 mt-2">
                    {NO_REGISTERED_USERS_MESSAGE}
                  </p>
                )}
              </div>

              {participants.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Participants ({participants.length})
                  </label>
                  <div className="space-y-2">
                    {participants.map((participant) => (
                      <div
                        key={participant.email}
                        className="flex items-center justify-between bg-gray-50 px-4 py-2 rounded-lg border border-gray-200"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-gray-700">{participant.email}</span>
                          <button
                            onClick={() => handleToggleRole(participant.email)}
                            className={[
                              'inline-flex items-center px-2 py-0.5 rounded-full',
                              'text-xs font-medium cursor-pointer transition-colors',
                              participant.role === 'approver'
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-gray-200 text-gray-700 hover:bg-gray-300',
                            ].join(' ')}
                            title="Click to toggle role"
                          >
                            {participant.role === 'approver' ? 'Approver' : 'Reviewer'}
                          </button>
                        </div>
                        <button
                          onClick={() => handleRemoveReviewer(participant.email)}
                          className="text-red-600 hover:text-red-700 p-1 hover:bg-red-50 rounded"
                          aria-label={`Remove ${participant.email}`}
                        >
                          <XMarkIcon className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <ParticipantWarnings
                participantCount={participants.length}
                hasApprover={hasApprover}
              />
            </div>

            <SubmitModalFooter
              participants={participants}
              submittingReview={submittingReview}
              onCancel={() => setShowSubmitModal(false)}
              onSubmit={handleSubmitForReview}
            />
          </div>
        </div>
      )}

    </div>
  );
};